import { useCallback, useState } from "react"
import { ImageDropzone } from "components/ui/ImageDropzone/ImageDropzone"
import { EditImageModal } from "components/ui/modals/EditImageModal/EditImageModal"
import { useMessage } from "hooks/message.hook"
import { useDropzone } from "react-dropzone"
import style from "./Image.module.scss"

const { dropZoneStyle, dropZoneError, imageBlock, imageWrapper } = style

const MAX_IMAGE_SIZE_MB = Number(import.meta.env.VITE_MAX_IMAGE_SIZE_MB) || 15
const MAX_IMAGE_SIZE_BYTES = MAX_IMAGE_SIZE_MB * 1024 * 1024

type Props = {
	onImageSave: (croppedUrl: string | null) => void
	croppedImageUrl: string | null
	showValidation: boolean
}

export const Image = ({ onImageSave, croppedImageUrl, showValidation }: Props) => {
	const { error } = useMessage()
	const [isModalOpen, setModalOpen] = useState(false)
	const [uploadedImage, setUploadedImage] = useState<File | null>(null)

	const onDrop = useCallback((acceptedFiles: File[]) => {
		if (acceptedFiles.length > 0) {
			setUploadedImage(acceptedFiles[0])
			setModalOpen(true)
		}
	}, [])

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop,
		accept: { "image/*": [] },
		maxSize: MAX_IMAGE_SIZE_BYTES,
		onDropRejected: () => error(`Image size must not exceed ${MAX_IMAGE_SIZE_MB}MB`),
	})

	const handleSave = (croppedUrl: string) => {
		onImageSave(croppedUrl)
		setModalOpen(false)
	}

	return (
		<>
			{croppedImageUrl ? (
				<div className={imageBlock}>
					<div className={imageWrapper}>
						<img src={croppedImageUrl} alt="Model image preview" />
					</div>
					<button onClick={() => onImageSave(null)}>Remove image</button>
				</div>
			) : (
				<ImageDropzone
					getInputProps={getInputProps}
					getRootProps={getRootProps}
					isDragActive={isDragActive}
					className={`${dropZoneStyle} ${showValidation && !croppedImageUrl ? dropZoneError : ""}`}
				/>
			)}
			<EditImageModal
				isModalOpen={isModalOpen}
				setModalClose={() => setModalOpen(false)}
				image={uploadedImage}
				onSave={handleSave}
			/>
		</>
	)
}
